(function initNuFiltersLite() {
  if (window.NuFiltersLite) return;

  const numberFormatter = new Intl.NumberFormat();

  function formatCount(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '0';
    return numberFormatter.format(number);
  }

  function getSearchParams(search = window.location.search) {
    return new URLSearchParams(search || '');
  }

  function getQueryParam(name, search = window.location.search) {
    const params = getSearchParams(search);
    return params.get(name);
  }

  function getPositiveIntParam(name, fallback, search = window.location.search) {
    const raw = getQueryParam(name, search);
    const parsed = Number(raw);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
    return fallback;
  }

  // Runs `worker` over `items` with at most `limit` calls in flight at once, preserving
  // result order regardless of resolution order (a fixed pool of workers each pulls the
  // next index off a shared cursor). Shared by nu--spec-filters.js and
  // nu--products-toolbar.js so a large collection's cross-page fetches don't fire 100+
  // concurrent Section Rendering requests at once.
  async function runWithConcurrency(items, limit, worker) {
    const results = new Array(items.length);
    let nextIndex = 0;
    const poolSize = Math.max(1, Math.min(limit, items.length));

    async function runOneWorker() {
      for (;;) {
        const currentIndex = nextIndex;
        nextIndex += 1;
        if (currentIndex >= items.length) return;
        results[currentIndex] = await worker(items[currentIndex], currentIndex);
      }
    }

    await Promise.all(Array.from({ length: poolSize }, runOneWorker));
    return results;
  }

  // Fixed 5-page window centered on the current page, clamped at the collection
  // boundaries — mirrors snippets/nu--pagination.liquid's start_page/end_page logic
  // exactly (no ellipsis), so client-side pagination never visually disagrees with the
  // server-rendered version. Shared by nu--spec-filters.js and nu--products-toolbar.js —
  // previously each kept its own copy, written differently enough that a future edit to
  // one could silently diverge from the other and from nu--pagination.liquid.
  function getPaginationPageItems(currentPage, totalPages) {
    let startPage = 1;
    let endPage = totalPages;
    if (totalPages > 5) {
      startPage = currentPage - 2;
      endPage = currentPage + 2;
      if (startPage < 1) {
        const shift = 1 - startPage;
        startPage = 1;
        endPage += shift;
      }
      if (endPage > totalPages) {
        const shift = endPage - totalPages;
        endPage = totalPages;
        startPage -= shift;
      }
      if (startPage < 1) startPage = 1;
    }
    const items = [];
    for (let page = startPage; page <= endPage; page += 1) items.push(page);
    return items;
  }

  // snippets/nu--pagination.liquid only renders its <nav> when paginate.pages > 1, so a collection
  // whose product count fits in one default-size page (nu_rows_default) ships NO pagination
  // markup at all — just the empty .nu-products-table__pagination-wrap host. Anything that later
  // needs client-side pagination (a smaller rows-per-page, a filtered result set larger than one
  // page) therefore can't rely on finding a server-rendered <nav> or on cloning its markup; it
  // has to create both itself (H-40). Shared by nu--spec-filters.js so the container/skeleton
  // are defined once instead of each file growing its own copy.

  // Returns the existing pagination <nav> inside the results area, or creates an empty hidden
  // one inside the pagination host. null when the page has no pagination host at all.
  function ensurePaginationWrap(ariaLabel) {
    const existingNav = document.querySelector('.main-products-grid__results nav[aria-label]');
    if (existingNav) return existingNav;

    const paginationHost = document.querySelector('.main-products-grid__results .nu-products-table__pagination-wrap');
    if (!paginationHost) return null;

    const nav = document.createElement('nav');
    nav.setAttribute('aria-label', ariaLabel || 'Pagination');
    nav.hidden = true;
    paginationHost.appendChild(nav);
    return nav;
  }

  // Keep in sync with the inline SVGs in snippets/nu--pagination.liquid (same note as the
  // PAGINATION_*_ICON_SVG constants in nu--products-toolbar.js — {% render %} isn't available
  // client-side, so these are maintained by hand).
  const PAGINATION_ICON_SVG = {
    prev: '<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false" role="presentation" class="icon"><path d="m6.797 11.625 8.03-8.03 1.06 1.06-6.97 6.97 6.97 6.97-1.06 1.06z"/></svg>',
    next: '<svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false" role="presentation" class="icon"><path d="m9.693 4.5 7.5 7.5-7.5 7.5" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>',
    first: '<svg width="24" height="24" viewBox="0 0 24 24" focusable="false" role="presentation" class="icon icon--pagination-first"><path d="M4.5 4.5v15" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/><path d="m6.797 11.625 8.03-8.03 1.06 1.06-6.97 6.97 6.97 6.97-1.06 1.06z" fill="currentColor"/></svg>',
    last: '<svg width="24" height="24" viewBox="0 0 24 24" focusable="false" role="presentation" class="icon icon--pagination-last"><path d="m9.693 4.5 7.5 7.5-7.5 7.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M19.5 4.5v15" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/></svg>',
  };

  // Builds the same <ul class="... js-pagination"> structure snippets/nu--pagination.liquid
  // renders (first / prev / mobile position / next / last, no page-number items), with every
  // arrow in its disabled state. Callers enable the arrows and insert the page numbers
  // themselves, exactly as they do on top of the server-rendered markup.
  // labels: { first, prev, next, last } — visually-hidden link text.
  function buildPaginationSkeleton(labels = {}) {
    const ul = document.createElement('ul');
    ul.className = 'pagination relative flex flex-wrap justify-center items-center justify-between w-full mx-auto js-pagination';

    const buildArrowItem = ({
      itemClass, linkClass, iconKey, label, labelFirst,
    }) => {
      const li = document.createElement('li');
      li.className = itemClass;
      li.hidden = true;
      const link = document.createElement('a');
      link.className = linkClass;
      link.setAttribute('role', 'link');
      link.setAttribute('aria-disabled', 'true');
      const iconSpan = document.createElement('span');
      iconSpan.className = 'pagination__arrow-icon';
      iconSpan.setAttribute('aria-hidden', 'true');
      iconSpan.innerHTML = PAGINATION_ICON_SVG[iconKey];
      const labelSpan = document.createElement('span');
      labelSpan.className = 'visually-hidden';
      labelSpan.textContent = label;
      if (labelFirst) link.append(labelSpan, iconSpan);
      else link.append(iconSpan, labelSpan);
      li.appendChild(link);
      return li;
    };

    ul.appendChild(buildArrowItem({
      itemClass: 'pagination__item pagination__item--arrow pagination__item--arrow-first',
      linkClass: 'pagination__arrow pagination__arrow--first hover:lighten flex text-current items-center has-ltr-icon',
      iconKey: 'first',
      label: labels.first || 'First page',
    }));
    ul.appendChild(buildArrowItem({
      itemClass: 'pagination__item pagination__item--arrow',
      linkClass: 'pagination__arrow pagination__arrow--prev hover:lighten flex text-current items-center has-ltr-icon',
      iconKey: 'prev',
      label: labels.prev || 'Previous',
    }));

    const positionLi = document.createElement('li');
    positionLi.className = 'pagination__item text-center md:hidden font-bold';
    ul.appendChild(positionLi);

    ul.appendChild(buildArrowItem({
      itemClass: 'pagination__item pagination__item--arrow',
      linkClass: 'pagination__arrow pagination__arrow--next hover:lighten flex justify-end text-current items-center has-ltr-icon js-pagination-load-more',
      iconKey: 'next',
      label: labels.next || 'Next',
      labelFirst: true,
    }));
    ul.appendChild(buildArrowItem({
      itemClass: 'pagination__item pagination__item--arrow pagination__item--arrow-last',
      linkClass: 'pagination__arrow pagination__arrow--last hover:lighten flex justify-end text-current items-center has-ltr-icon',
      iconKey: 'last',
      label: labels.last || 'Last page',
      labelFirst: true,
    }));

    return ul;
  }

  // Single shared counter for the "version" carried on nu:r4q:refresh / nu:r4q:refresh:done
  // (see nu--products-toolbar.js's r4qBusyVersion and nu--spec-filters.js's applySelections()
  // dispatch). Both files independently need a monotonically increasing id to tell a stale
  // "done" signal apart from the request it actually belongs to — if each kept its own counter
  // starting from 0, the two sequences could collide or compare incorrectly against each other
  // (a request from one side is neither definitely newer nor older than the other side's last
  // number). Routing every version through this one shared sequence means whichever side reads
  // it back can just trust it directly, with no cross-counter comparison needed.
  let r4qRefreshVersionCounter = 0;
  function nextR4QRefreshVersion() {
    r4qRefreshVersionCounter += 1;
    return r4qRefreshVersionCounter;
  }

  window.NuFiltersLite = {
    formatCount,
    getQueryParam,
    getPositiveIntParam,
    runWithConcurrency,
    getPaginationPageItems,
    ensurePaginationWrap,
    buildPaginationSkeleton,
    nextR4QRefreshVersion,
  };
})();
